import _ from 'lodash'
import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { InMemoryCache, defaultDataIdFromObject, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import envFromHostname from '@toasttab/env-from-hostname'

import { getAuthLink } from './authentication/authentication-helpers'
import { authenticationResolvers } from './authentication/authentication-resolvers'
import getHttpLink from './links/http-link'
import getErrorLink from './links/error-link'
import getRestLink from './links/rest-link'
import { getRetryLinkFactory } from './links/retry-link'
import introspectionQueryResultData from './generated/fragmentTypes.json'
import { menuResolvers } from './menu/menu-resolvers'
import { cartResolvers } from './cart/cart-resolvers'
import { geolocationResolvers } from './geolocation/geolocation-resolvers'
import { managementGroupResolvers } from './management-group/management-group-resolvers'

const config = {}
const defaultData = {
  data: {
    activeMenu: window.location.hash.split('#')[1] || null
  }
}

const doNotCacheThisType = () => null
const dataIdFromObjectMap = {
  'CompletedOrder': defaultDataIdFromObject,
  'Menu': defaultDataIdFromObject,
  'MenuItem': object => `${object.guid}${object.itemGroupGuid || ''}`,
  'MenuGroup': defaultDataIdFromObject,
  'SelectionItem': doNotCacheThisType,
  'SelectionModifierGroup': doNotCacheThisType,
  'SelectionModifier': doNotCacheThisType,
  'ModifierGroup': doNotCacheThisType,
  'Modifier': doNotCacheThisType,
  // CartResponse and Cart share a GUID, so we need a way
  // to differentiate the two responses
  'CartResponse': object => object.cart.guid + object.__typename,
  'AddressSuggestion': object => `${object.latitude}+${object.longitude}`,
  'LoyaltyRedemption': object => object.redemptionGuid,
  _default: object => object.guid || null
}

export const dataIdFromObject = object => {
  const idMapper = dataIdFromObjectMap[object.__typename] || dataIdFromObjectMap._default
  return idMapper(object)
}

export const fragmentMatcher = new IntrospectionFragmentMatcher({ introspectionQueryResultData })
export const cache = new InMemoryCache({ dataIdFromObject, fragmentMatcher })
cache.writeData(defaultData)
cache.deleteKeyByRegex = regex => {
  Object.keys(cache.data.data).forEach(key => {
    if (key.match(regex)) {
      cache.data.delete(key)
    }
  })
}

config.cache = cache

const ooGlobals = window.OO_GLOBALS || {}
const featureFlags = ooGlobals.enabledFeatureFlags || []
const enableRetries = featureFlags.includes('oo-v3-allow-apollo-retry')
const env = envFromHostname(window.location.hostname)
// TODO Need to change this to support sandbox once the BFF can be deployed there.
const prefix = env === 'prod' ? 'ws-api' : 'ws-preprod-api.eng'

config.GATEWAY_BASE_URI = _.trimEnd(
  process.env.GATEWAY_BASE_URI || ooGlobals.gatewayBaseUri || `https://${prefix}.toasttab.com`, '/')
config.BFF_BASE_URI = _.trimEnd(
  process.env.BFF_BASE_URI || ooGlobals.bffBaseUri || `${config.GATEWAY_BASE_URI}/consumer-app-bff/v1`, '/')
config.OO_BASE_URI = _.trimEnd(
  process.env.OO_BASE_URI || ooGlobals.ooBaseUri || `${config.GATEWAY_BASE_URI}/online-ordering/v1`, '/')
config.RESTAURANT_GUID = ooGlobals.restaurantGuid || ''

export const resolvers = [
  cartResolvers,
  authenticationResolvers,
  menuResolvers,
  geolocationResolvers,
  managementGroupResolvers
]

const linkFactories = [getRestLink, getRetryLinkFactory(enableRetries), getErrorLink, getAuthLink, getHttpLink]

config.client = new ApolloClient({
  link: ApolloLink.from(linkFactories.map(fn => fn(config))),
  cache: config.cache,
  resolvers,
  name: process.env.PKG_NAME,
  version: process.env.PKG_VERSION
})

config.client.onResetStore(() => {
  cache.writeData(defaultData)
})

export default config.client
